import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest, logoutRequest } from '../authConfig';
import { decodeJwtPayload } from './authApi';
import { clearAuthenticatedMarker } from './authSession';

const CLIENT_ID = process.env.REACT_APP_CLIENT_ID;
const TENANT_ID = process.env.REACT_APP_TENANT_ID;

export const FORCE_RELOGIN_STORAGE_KEY = 'nettles_force_relogin';

function getMsalIdTokenFromCache(account) {
  const homeAccountId = account?.homeAccountId;
  if (!homeAccountId || typeof window === 'undefined') {
    return null;
  }

  const clientId = CLIENT_ID?.toLowerCase();

  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (!key) {
      continue;
    }

    const normalizedKey = key.toLowerCase();
    if (!normalizedKey.includes('idtoken')) {
      continue;
    }

    if (!key.includes(homeAccountId)) {
      continue;
    }

    if (clientId && !normalizedKey.includes(clientId)) {
      continue;
    }

    try {
      const parsed = JSON.parse(window.localStorage.getItem(key));
      const secret = parsed?.secret;
      if (typeof secret === 'string' && secret.split('.').length === 3) {
        return secret;
      }
    } catch {
      // Try the next cache entry.
    }
  }

  return null;
}

async function resolveIdToken(instance, account) {
  const cachedIdToken = getMsalIdTokenFromCache(account);
  if (cachedIdToken) {
    return cachedIdToken;
  }

  try {
    const tokenResult = await instance.acquireTokenSilent({
      scopes: ['openid', 'profile'],
      account,
      forceRefresh: true,
    });

    if (tokenResult.idToken) {
      return tokenResult.idToken;
    }
  } catch (error) {
    if (!(error instanceof InteractionRequiredAuthError)) {
      console.warn('Could not refresh ID token before logout:', error.message);
    }
  }

  try {
    const tokenResult = await instance.acquireTokenSilent({
      ...loginRequest,
      account,
    });

    return tokenResult.idToken || null;
  } catch {
    return null;
  }
}

/**
 * Microsoft only accepts the ID token `login_hint` claim as `logout_hint`.
 * Without it, the sign-out account picker may appear when multiple browser
 * accounts are signed in — expected Microsoft behaviour.
 */
function resolveLoginHintClaim(account, idToken) {
  if (account?.idTokenClaims?.login_hint) {
    return account.idTokenClaims.login_hint;
  }

  if (idToken) {
    const payload = decodeJwtPayload(idToken);
    if (payload?.login_hint) {
      return payload.login_hint;
    }
  }

  return null;
}

export function resolveCurrentAccount(instance, accounts = []) {
  const activeAccount = instance.getActiveAccount();
  if (activeAccount) {
    return activeAccount;
  }

  const matchedAccount = accounts.find((account) => {
    const tokenAud = account?.idTokenClaims?.aud;
    const audiences = Array.isArray(tokenAud) ? tokenAud : [tokenAud];
    const isValidTenant = account?.tenantId === TENANT_ID;
    const isValidClientId = audiences.includes(CLIENT_ID);
    return isValidTenant && isValidClientId;
  });

  return matchedAccount || accounts[0] || null;
}

export function shouldForceRelogin() {
  try {
    return sessionStorage.getItem(FORCE_RELOGIN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function markForceRelogin() {
  try {
    sessionStorage.setItem(FORCE_RELOGIN_STORAGE_KEY, 'true');
  } catch {
    // noop
  }
}

export function clearForceReloginFlag() {
  try {
    sessionStorage.removeItem(FORCE_RELOGIN_STORAGE_KEY);
  } catch {
    // noop
  }
}

/**
 * Federated sign-out via Microsoft Entra ID.
 * MSAL clears local tokens, ends the server session, then redirects back to the app.
 * AuthenticationCheck shows the sign-in screen with prompt=login when the user returns.
 */
export async function logoutUser(instance, account) {
  if (!account) {
    return;
  }

  try {
    window.__currentUserDisplayName = '';
  } catch {
    // noop
  }

  const activeAccount =
    instance.getAccountByHomeId(account.homeAccountId) || account;

  const idToken = await resolveIdToken(instance, activeAccount);
  const loginHintClaim = resolveLoginHintClaim(activeAccount, idToken);

  markForceRelogin();
  clearAuthenticatedMarker();

  const request = {
    ...logoutRequest,
    account: activeAccount,
  };

  if (idToken) {
    request.idTokenHint = idToken;
  }

  if (loginHintClaim) {
    request.logoutHint = loginHintClaim;
  }

  await instance.logoutRedirect(request);
}
